import { useCallback } from "react";
import { Alert, Button } from "react-bootstrap";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useParams } from "react-router-dom";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import { useCartPricing } from "../../hooks/use-cart-pricing";
import { useSavedCart } from "../../hooks/use-saved-cart";
import { formatCurrency } from "../../lib/format-currency";
import "./CartPaymentPage.scss";

interface CartPaymentPageProps {}

const CartPaymentPage: React.FC<CartPaymentPageProps> = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const { cart, isLoading: isCartLoading } = useSavedCart(cartId);
  const { totalX100, stripePaymentLink } = useCartPricing(cart);

  const openPaymentPage = useCallback(() => {
    if (!stripePaymentLink) {
      return;
    }
    window.open(stripePaymentLink, "_blank");
  }, [stripePaymentLink]);

  if (isCartLoading) {
    return <LoadingMask message="Loading cart..." />;
  }

  return (
    <div className="cart-payment-page">
      <div className="page-header">
        Purchase Contact List
        <div className="page-subheader">
          You are about to purchase a contact list! Please review the details
          below.
        </div>
      </div>
      <div className="disclosure-container">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Payments</h5>
            <div className="card-text">
              You will be redirected to a secure payment page where you can
              purchase your contacts ({cart?.items.length ?? 0}) for{" "}
              {formatCurrency(totalX100)}.
              <br />
              <br /> We use{" "}
              <a href="https://stripe.com" target="_blank" rel="noreferrer">
                Stripe
              </a>{" "}
              to process payments. None of your payment information is ever
              stored by VCDB.
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">What happens after I purchase?</h5>
            <div className="card-text">
              You'll receive an email with your contact list as a CSV file.
              <br />
              <br />
              Soon afterwards, all contacts will appear in your VCDB contact
              library.
            </div>
          </div>
        </div>
      </div>
      <div className="main-cta">
        <Alert variant="warning">
          Please copy the current URL. You'll need to paste it into the next
          page. (We're running lean right now - we'll fix this soon!)
        </Alert>
        <Button
          className="icon-button"
          variant="primary"
          onClick={openPaymentPage}
        >
          Purchase Contact List <FaExternalLinkAlt className="icon-right" />
        </Button>
      </div>
    </div>
  );
};

export default CartPaymentPage;
